import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Keeps the buy-button CTA in sync with the selected variant.
 *
 * THE PROBLEM. blocks/buy-buttons.liquid decides WHICH CTA to render — the product form, the
 * notify-me / enquire form, or "Book an appointment" — server side, once, from
 * `selected_or_first_available_variant`. On a variant change, variant-picker.js morphs only the
 * <variant-picker> element and then dispatches ThemeEvents.variantUpdate carrying the freshly
 * fetched document; every other component is expected to listen and morph itself. product-form.js
 * does listen, but it only disables/enables the add-to-cart button, morphs that button, and hides
 * the accelerated-checkout container — it never re-asks which BRANCH should be on the page. And
 * when the notify branch is rendered there is no <product-form-component> in the DOM at all, so
 * nothing is listening. Result: the CTA is frozen at whatever the first render chose.
 *
 * Two ways that surfaces, both seen on the Smith helmets (mixed stock matrix):
 *   in stock -> out of stock : dead grey button, with the "In stock" line stranded beneath it.
 *   out of stock -> in stock : a purchasable variant still offering a waitlist. The costly one.
 *
 * THE FIX. buy-buttons.liquid stamps the chosen branch on this element as `data-cta`. We compare
 * it against the incoming render and morph ONLY when the branch actually changed. When it hasn't
 * — which is every variant switch on a normal product — we do nothing and leave Horizon's own
 * handlers alone, so the accelerated-checkout iframe and the quantity selector are never
 * needlessly torn down.
 */
class BuyButtonsSwap extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #onVariantUpdate = (event) => {
    const incoming = event?.detail?.data?.html;
    if (!incoming) return;

    // Combined listings can swap the product out from under us; otherwise ignore events fired by
    // a different product on the same page (quick-add dialogs, recommendations).
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    // Match our own block instance first — a page can hold more than one buy-buttons block.
    const blockId = this.dataset.blockId;
    const next =
      (blockId && incoming.querySelector(`buy-buttons-swap[data-block-id="${blockId}"]`)) ||
      incoming.querySelector('buy-buttons-swap');

    if (!next || next.dataset.cta === this.dataset.cta) return;

    this.dataset.cta = next.dataset.cta || '';
    morph(this, next, { childrenOnly: true });
    this.#reviveScripts();
  };

  /**
   * A <script> inserted by morph never executes — it comes from a parsed Document, not the parser.
   * snippets/enquire-to-order.liquid needs its script to run to bind the popup trigger, so swap
   * each one for a freshly created element. Both snippets that land here are written to be safe to
   * run more than once (enquire guards on window.fdEnquireBound; notify-me-form.liquid carries no
   * script at all, deliberately).
   */
  #reviveScripts() {
    for (const stale of this.querySelectorAll('script')) {
      const fresh = document.createElement('script');
      for (const { name, value } of stale.attributes) fresh.setAttribute(name, value);
      fresh.textContent = stale.textContent;
      stale.replaceWith(fresh);
    }
  }
}

if (!customElements.get('buy-buttons-swap')) {
  customElements.define('buy-buttons-swap', BuyButtonsSwap);
}